import { MenuItem, Select } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { values } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ConsumerBatteryPriceFormat from 'src/components/ConsumerBatteryPrice';
import CurrencyFormat from 'src/components/currency/Currency';
import ProductAvailabilityLabel from 'src/components/productAvailabilityLabel';
import RootStore from 'src/stores/RootStore';
import { TProductModel, TBatteryModel } from 'src/stores/types/ProductTypes';

interface SelectBatteryProps extends WithStyles<typeof styles> {
    product: TProductModel;
    store?: typeof RootStore.Type;
    selectedBatteryCode: string;
    setSelectedBattery: (selected: string) => void;
}

const SelectBattery = inject('store')(
    observer(({ classes, product, store, selectedBatteryCode, setSelectedBattery }: SelectBatteryProps) => {
        const [isOpen, setOpen] = useState(false);

        const hideBatteryPriceDifference: boolean = store!.config.isConsumer;

        // These series can't be ordered without a battery
        const serie = product.serie?.toLowerCase() ?? '';
        const isBrekr = serie === 'brekr';
        const hideNoBatteryOption = serie.includes('elan') || isBrekr;

        // Brekr bikes have no "None" option, so preselect a battery: one in stock, otherwise one with
        // an availability date, otherwise the first one
        useEffect(() => {
            if (!isBrekr || selectedBatteryCode) {
                return;
            }
            const batteries = product.batteryOptions ?? [];
            const defaultBattery =
                batteries.find(battery => battery.productInformation.inStock) ??
                batteries.find(battery => !!battery.productInformation.availableFrom) ??
                batteries[0];
            if (defaultBattery) {
                setSelectedBattery(defaultBattery.id);
            }
        }, [product.id, isBrekr, selectedBatteryCode]);

        const getOption = (value: any) => {
            const foundOption = values(product.batteryOptions).find(battery => battery.id === value);
            return foundOption ? (
                foundOption.productInformation.size ?? foundOption.productInformation.name
            ) : (
                <span>
                    <FormattedMessage id="product.accu.empty" />
                </span>
            );
        };

        // Use the cheapest battery price for free upgrade battery promotion
        let cheapestBattery: TBatteryModel | null;
        if (product.isBatteryUpgradePromotion) {
            cheapestBattery = product.cheapestBattery ?? null;
        }

        return (
            <Select
                data-testid="batterySelector"
                labelId='battery-item'
                open={isOpen}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={selectedBatteryCode}
                name="selectedAccuCode"
                className={classes.select}
                displayEmpty={false}
                renderValue={(value: any) => getOption(value)}
                onChange={e => setSelectedBattery(e.target.value as string)}
            >

                {!hideNoBatteryOption && (
                    <MenuItem key="empty" value="empty" data-testid="no-battery">
                        <FormattedMessage id="product.accu.empty">{text => <em>{`${text}`}</em>}</FormattedMessage>
                    </MenuItem>
                )}

                {product
                    .batteryOptions!.slice()
                    .sort()
                    .map(({ productInformation, id, calculatedPrice }, index) => (
                        <MenuItem
                            key={id}
                            value={id}
                            data-testid="battery"
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                            // Disable select cheapest battery for the fre upgrade battery bikes
                            // disabled={cheapestBattery?.id === id}
                        >
                            <span>
                                <span>{productInformation.size ?? productInformation.name}</span>
                                {isOpen && !hideBatteryPriceDifference ? (
                                    <span>
                                        <span className={classes.addedPrice}>{'(+'}</span>

                                        {(cheapestBattery?.calculatedPrice ?? calculatedPrice) !== null &&
                                            (store!.config.isConsumer ? (
                                                <ConsumerBatteryPriceFormat
                                                    batteryList={product.batteryOptions!.toJSON()}
                                                    currentIndex={index}
                                                    value={cheapestBattery?.calculatedPrice! ?? calculatedPrice}
                                                />
                                            ) : (
                                                <span>
                                                    <CurrencyFormat
                                                        value={cheapestBattery?.calculatedPrice! ?? calculatedPrice}
                                                    />
                                                </span>
                                            ))}

                                        <span>{')'}</span>
                                    </span>
                                ) : null}
                            </span>
                            <ProductAvailabilityLabel
                                inStock={productInformation.inStock}
                                availableFrom={productInformation.availableFrom}
                                stockQuantity={productInformation.stockQuantity}
                            />
                        </MenuItem>
                    ))}
            </Select>
        );
    }),
);

function styles() {
    return createStyles({
        select: {
            overflow: 'hidden',
        },
        addedPrice: {
            marginLeft: '5px',
        },
    });
}

export default withStyles(styles)(SelectBattery);
